import React, { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import { isArrayEmpty } from "../../../../libs/common";

import {
  StoreInfo,
  getStoreList,
} from "../../../../services/rentalStockServices";
import { getGoodsRentalSatisfactionList } from "../../../../services/goodsRentalServices";
import { PageableRequest } from "../../../../services/talkServices";

import useInfiniteScroll, {
  INFINITE_CODE,
} from "../../../../hooks/useInfiniteScroll";
import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import CommonSelect from "../../../../components/Select";
import StickyHeader from "../../../../components/Layout/StickyHeader";
import { AuthHeader } from "../../../../components/Layout/Auth/Header";
import RentalEachReview from "../../../../components/Manager/Rental/EachReview";

import { NoDataIcon } from "../../../Center";
import { RefreshIcon } from "../../../../hooks/useStoreHeader";
import { TalkFindBuildingDownArrow } from "../../../Talk";
import {
  ManagerReportActiveCheck,
  ManagerReportInactiveCheck,
} from "../../Report";

type Props = {};

export default function ManagerRentalManagerSatisfaction(props: Props) {
  const navigate = useNavigate();
  const [store, setStore] = useState<StoreInfo>();
  const [storeList, setStoreList] = useState<StoreInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({
    isLoading,
    testId: "manager-satisfaction",
  });
  const [satisfactionList, setSatisfactionList] = useState<any[]>([]);
  const [isReply, setIsReply] = useState<boolean>(true);
  const [simplePopupComponent, { openError }] = useSimplePopup({});
  const [getSatisfactionFail, setGetSatisfactionFail] =
    useState<boolean>(false);

  const handleRefresh = () => {
    resetScroll();
    setGetSatisfactionFail(false);
    loadSatisfactionList({});
  };

  const loadStoreList = async () => {
    setIsLoading(true);
    try {
      const result = await getStoreList();
      if (result.status === HttpStatusCode.Ok) {
        setStoreList(result.data.items);
      }
    } catch (e) {
      console.log("", e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSatisfactionList = async ({
    page = 1,
    pageSize = 10,
  }: PageableRequest) => {
    let returnCount = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const result = await getGoodsRentalSatisfactionList({
        storeId: store?.storeId ?? undefined,
        isReply,
        page,
        pageSize,
      });
      if (result.status === HttpStatusCode.Ok) {
        if (page === 1) {
          setSatisfactionList(result.data.items ?? []);
        } else {
          setSatisfactionList((prev) => [
            ...prev,
            ...(result.data.items ?? []),
          ]);
        }
        returnCount = (result.data.items ?? []).length;
      } else {
        setSatisfactionList([]);
      }
    } catch (e: any) {
      openError(e, "이용자 만족도 평가 조회에 실패했습니다.");
    } finally {
      setIsLoading(false);
      if (returnCount === -1) {
        setGetSatisfactionFail(true);
        setSatisfactionList([]);
      }
    }
    return returnCount;
  };

  const [callbackArgs, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return loadSatisfactionList({
        ...args,
      });
    },
    isLoading: isLoading,
  });

  useEffect(() => {
    handleRefresh();
  }, [store, isReply]);

  useEffect(() => {
    loadStoreList();
  }, []);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="만족도 평가"
        onBackClick={() => navigate("/manager/rental-manager")}
        className="px-5"
        rightContent={
          <div onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        }
      />

      <AuthHeader ruleClassName="h-1.5">
        <div className="px-5">
          <CommonSelect
            defaultValue={"전체 대여 장소"}
            arrowIcon={<TalkFindBuildingDownArrow />}
            value={store?.storeId ?? ""}
            unUsePadding
            useTruncate
            optionList={
              [
                { value: "", label: "전체 대여 장소" },
                ...(storeList?.map((store) => ({
                  value: store.storeId,
                  label: store.storeName,
                })) ?? []),
              ] ?? []
            }
            onChange={(target) => {
              const selectedStore = storeList.find(
                (store) => store.storeId === target.value
              );
              setStore(selectedStore);
            }}
            className="text-3xl font-medium leading-[38px] tracking-[-1.2px]"
            optionOrder="between"
            optionWidth="240px"
            wrapperClassName="w-fit max-w-[calc(100vw-40px)]"
            variant="selectBuilding"
          />
        </div>
      </AuthHeader>

      <div>
        <div className="p-5">
          <div className="text-lg font-medium">평가 목록</div>
          <div
            className={clsx([
              "flex gap-1 items-center",
              {
                "text-brand-primary-magenta": !isReply,
                "text-brand-neutral-40": isReply,
              },
            ])}
            onClick={() => setIsReply((prev) => !prev)}
          >
            {!isReply ? (
              <ManagerReportActiveCheck />
            ) : (
              <ManagerReportInactiveCheck />
            )}
            미답변
          </div>
        </div>

        <div className="px-5 flex flex-col gap-[15px]">
          {!isArrayEmpty(satisfactionList) ? (
            satisfactionList?.map((review) => (
              <RentalEachReview
                key={review.etcSatisfactionId}
                review={review}
                loadReview={handleRefresh}
                useNickname
              />
            ))
          ) : (
            <div
              className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2 whitespace-pre-line text-center"
              data-testid="satisfaction-no-data-div"
            >
              <NoDataIcon />
              {getSatisfactionFail
                ? "이용자 만족도 평가 조회에 실패 하였습니다.\n다시 시도해 주세요."
                : "등록된 이용자 만족도 평가가 없어요"}
            </div>
          )}
        </div>
      </div>
      {simplePopupComponent}
    </div>
  );
}
